import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { WP_URL } from './V2Layout';
import './PricingV2.css';

/**
 * PricingV2 — 3-tier pricing (Free / Pro / Business) with a Monthly/Yearly
 * toggle. All copy is i18n-driven (see `pricing` in the locale files) so it
 * stays in sync across FR/EN. Scoped under `.pv2` inside the V2 look.
 */
export default function PricingV2() {
  const { t } = useTranslation();
  const [yearly, setYearly] = useState(false);

  const perMo = t('pricing.per_mo');
  const period = yearly ? `${perMo}${t('pricing.billed_yearly_suffix')}` : perMo;

  const freeFeatures = t('pricing.plans.free.features', { returnObjects: true });
  const proFeatures = t('pricing.plans.pro.features', { returnObjects: true });
  const bizFeatures = t('pricing.plans.business.features', { returnObjects: true });

  // Bold features per plan, matched to the reference (indices into the features array).
  const featureCol = (items, boldIdx = []) => (
    <div className="pv2-features">
      {(Array.isArray(items) ? items : []).map((f, i) => (
        <div className={`pv2-feat ${boldIdx.includes(i) ? 'strong' : ''}`} key={i}>
          <Check /> <span>{f}</span>
        </div>
      ))}
    </div>
  );

  return (
    <section id="pricing" className="v2-section pv2">
      <div className="v2-container">
        {/* Billing toggle */}
        <div className="pv2-toggle v2-reveal" role="tablist" aria-label={t('pricing.title')}>
          <button
            type="button"
            role="tab"
            aria-selected={!yearly}
            className={`pv2-toggle-btn ${!yearly ? 'active' : ''}`}
            onClick={() => setYearly(false)}
          >
            {t('pricing.billing.monthly')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={yearly}
            className={`pv2-toggle-btn ${yearly ? 'active' : ''}`}
            onClick={() => setYearly(true)}
          >
            {t('pricing.billing.yearly')}
          </button>
        </div>

        <div className="pv2-list v2-reveal">
          {/* FREE */}
          <div className="pv2-card">
            <div className="pv2-left">
              <div className="pv2-name">{t('pricing.plans.free.name')}</div>
              <div className="pv2-price">
                {t('pricing.plans.free.price')}
                <span className="pv2-period">{t('pricing.plans.free.period')}</span>
              </div>
              <div className="pv2-sub">{t('pricing.plans.free.note')}</div>
            </div>
            {featureCol(freeFeatures, [0])}
            <div className="pv2-action" />
          </div>

          {/* PRO — featured */}
          <div className="pv2-card featured">
            <span className="pv2-highlight">{t('pricing.plans.pro.highlight')}</span>
            <div className="pv2-left">
              <div className="pv2-name">{t('pricing.plans.pro.name')}</div>
              <div className="pv2-price">
                {yearly ? t('pricing.plans.pro.price_yearly') : t('pricing.plans.pro.price_monthly')}
                <span className="pv2-period">{period}</span>
              </div>
              <div className="pv2-sub">
                {yearly ? t('pricing.plans.pro.sub_yearly') : t('pricing.plans.pro.sub_monthly')}
              </div>
            </div>
            {featureCol(proFeatures, [0, 1])}
            <div className="pv2-action">
              <a className="pv2-btn pv2-btn-primary" href={WP_URL} target="_blank" rel="noreferrer">
                {yearly ? t('pricing.plans.pro.cta_yearly') : t('pricing.plans.pro.cta_monthly')}
              </a>
              <div className="pv2-cancel">{t('pricing.plans.pro.cancel')}</div>
            </div>
          </div>

          {/* BUSINESS */}
          <div className="pv2-card">
            <div className="pv2-left">
              <div className="pv2-name">{t('pricing.plans.business.name')}</div>
              <div className="pv2-price">
                {yearly ? t('pricing.plans.business.price_yearly') : t('pricing.plans.business.price_monthly')}
                <span className="pv2-period">{period}</span>
              </div>
              <div className="pv2-sub">
                {yearly ? t('pricing.plans.business.sub_yearly') : t('pricing.plans.business.sub_monthly')}
              </div>
            </div>
            {featureCol(bizFeatures, [0])}
            <div className="pv2-action" />
          </div>
        </div>
      </div>
    </section>
  );
}
